// The rules behind the review deck and the archive, kept out of the components so they can be read
// and tested on their own.
//
// The shape of the thing: an observation Oreia has written is *pending* until you say something
// about it. Saying something files it — under what you said. That is the whole model, and both the
// deck (what is still unanswered) and the archive (what you answered, grouped by answer) are just
// two readings of the same verdict column.

import type { Observation } from '../api/portrait';

/** The verdicts the server stores, as they come back on a row. */
export type Verdict = 'affirmed' | 'rejected' | 'deferred' | 'forgotten';

/** What the API takes when recording one. Not the same words the column holds. */
export type VerdictAction = 'affirm' | 'reject' | 'defer';

export const VERDICT_FOR: Record<VerdictAction, Verdict> = {
  affirm: 'affirmed',
  reject: 'rejected',
  defer: 'deferred',
};

/**
 * Has this card been answered at all?
 *
 * Two columns can say yes, and both have to be read. `verdict` is the modern answer, but cards
 * affirmed before verdicts existed carry nothing there and only set `affirmed` — so checking the
 * verdict alone would put a card someone already agreed with back in the queue and ask them again.
 */
function answered(o: Observation): boolean {
  return o.verdict != null || o.affirmed;
}

/**
 * Cards still waiting on an answer, oldest noticing first.
 *
 * Oldest-first is deliberate. A queue you work through should not reshuffle under you every time the
 * nightly pass adds something — and the card that has been waiting longest is the one whose evidence
 * is furthest from being fresh in mind, so it is the one most worth asking about before it stops
 * meaning anything.
 */
export function pendingCards(observations: Observation[]): Observation[] {
  return observations
    .filter((o) => !answered(o))
    .slice()
    .sort((a, b) => (a.createdAt ?? '').localeCompare(b.createdAt ?? ''));
}

export interface ArchiveBuckets {
  /** "That's true." Oreia leans on these. */
  confirmed: Observation[];
  /** "That's not me." Oreia stops leading with these, and the generator learns from the no. */
  rejected: Observation[];
  /** "Not now." Out of the queue, not judged — the one verdict that is allowed to be reopened. */
  setAside: Observation[];
}

/**
 * Sort the history into the three answers someone can have given.
 *
 * Pending rows come back from the history endpoint too and are deliberately dropped here: the
 * archive is a record of what you decided, and a card you have not answered belongs in the deck.
 * Forgotten rows never arrive with readable text (the server clears the ciphertext), so they fall
 * out earlier, in the API layer's renderability check. That is the correct outcome — someone who
 * asked Oreia to forget something should not find it filed in an archive.
 *
 * Confirmed reads the same two columns `answered` does, and has to. The archive is now the only
 * place a confirmed card appears, so a card affirmed before verdicts existed would otherwise be
 * gone from every screen at once — filed nowhere, and still asserted about someone.
 */
export function bucketHistory(observations: Observation[]): ArchiveBuckets {
  return {
    confirmed: observations.filter((o) => o.verdict === 'affirmed' || (o.affirmed && o.verdict == null)),
    rejected: observations.filter((o) => o.verdict === 'rejected'),
    setAside: observations.filter((o) => o.verdict === 'deferred'),
  };
}

/** What to say after a verdict lands. Said in the person's frame, not the database's. */
export function verdictPing(action: VerdictAction): string {
  if (action === 'affirm') return 'Confirmed — Oreia will lean on this';
  if (action === 'reject') return "Noted — Oreia won't hold this about you";
  return 'Set aside — it will keep';
}

/** How far a drag has to travel before it counts as a verdict rather than a fidget. */
export const SWIPE_THRESHOLD = 84;

/**
 * Which way a finished drag pointed, if it went far enough to mean anything.
 *
 * Right is affirm and left is reject, which is the direction every card-sorting interface has used
 * since Tinder — worth following rather than being original about, because the muscle memory it
 * borrows is the entire benefit of having a swipe at all.
 */
export function swipeVerdict(dx: number): VerdictAction | null {
  if (dx >= SWIPE_THRESHOLD) return 'affirm';
  if (dx <= -SWIPE_THRESHOLD) return 'reject';
  return null;
}
